interface Rgb {
  r: number;
  g: number;
  b: number;
}

const hexToRgb = (hex: string): Rgb => {
  const normalized = hex.replace("#", "");
  const value = parseInt(normalized, 16);

  return {
    r: (value >> 16) & 255,
    g: (value >> 8) & 255,
    b: value & 255,
  };
};

const rgbToHex = ({ r, g, b }: Rgb) =>
  `#${[r, g, b]
    .map((channel) =>
      Math.max(0, Math.min(255, Math.round(channel)))
        .toString(16)
        .padStart(2, "0"),
    )
    .join("")}`;

const mix = (color: string, target: string, weight: number) => {
  const from = hexToRgb(color);
  const to = hexToRgb(target);

  return rgbToHex({
    r: from.r + (to.r - from.r) * weight,
    g: from.g + (to.g - from.g) * weight,
    b: from.b + (to.b - from.b) * weight,
  });
};

const relativeLuminance = (hex: string) => {
  const { r, g, b } = hexToRgb(hex);

  const [rs, gs, bs] = [r, g, b].map((channel) => {
    const c = channel / 255;
    return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
  });

  return 0.2126 * rs + 0.7152 * gs + 0.0722 * bs;
};

const readableOn = (hex: string) =>
  relativeLuminance(hex) > 0.4 ? "#0F172A" : "#FFFFFF";

export interface DerivedClubColors {
  primary: string;
  "on-primary": string;
  accent: string;
  "on-accent": string;
  secondary: string;
  "on-secondary": string;
  "stepper-surface": string;
  "on-stepper-surface": string;
}

const HEX_COLOR_PATTERN = /^#[0-9A-Fa-f]{6}$/;

export const deriveClubTheme = (
  primaryColor: string,
): DerivedClubColors | null => {
  if (!HEX_COLOR_PATTERN.test(primaryColor)) return null;

  const primary = primaryColor;
  const accent = mix(primary, "#000000", 0.18);
  const secondary = mix(primary, "#FFFFFF", 0.94);
  const stepperSurface = mix(primary, "#FFFFFF", 0.85);

  return {
    primary,
    "on-primary": readableOn(primary),
    accent,
    "on-accent": readableOn(accent),
    secondary,
    "on-secondary": accent,
    "stepper-surface": stepperSurface,
    "on-stepper-surface": accent,
  };
};
